// src/services/cosmetics_api.js
import axios from 'axios';

// ============================================
// API CONFIGURATION - DUAL APIS
// ============================================
const COSMETICS_API_URL = 'https://ourzestapp.online/oz_backend/cosmetics_module';
const MECHANICS_API_URL = 'https://ourzestapp.online/oz_backend/mechanics_module';

let authToken = null;
let currentUser = null;
let currentShop = null;

export const setAuthData = (user, shop) => {
  currentUser = user;
  currentShop = shop;
  if (user && user.token) authToken = user.token;
};

const getHeaders = () => ({
  'Content-Type': 'application/json',
  ...(authToken && { 'Authorization': `Bearer ${authToken}` })
});

// API Request with dynamic base URL
const apiRequest = async (baseUrl, endpoint, data) => {
  try {
    const response = await axios.post(`${baseUrl}/${endpoint}`, data, { headers: getHeaders() });
    return response.data;
  } catch (error) {
    console.error(`API Error (${endpoint}):`, error);
    return { status: 'error', message: error.message, data: [] };
  }
};

// Cosmetics API calls
const cosmeticsRequest = async (endpoint, data) => {
  return apiRequest(COSMETICS_API_URL, endpoint, data);
};

// Mechanics API calls
const mechanicsRequest = async (endpoint, data) => {
  return apiRequest(MECHANICS_API_URL, endpoint, data);
};

// ============================================
// HELPER FUNCTIONS
// ============================================
const safeNumber = (value, defaultValue = 0) => {
  if (value === null || value === undefined || value === '') return defaultValue;
  const num = parseFloat(value);
  return isNaN(num) ? defaultValue : num;
};

const safeArray = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (response.data && Array.isArray(response.data)) return response.data;
  if (response.data && response.data.data && Array.isArray(response.data.data)) return response.data.data;
  if (Array.isArray(response.data)) return response.data;
  return [];
};

// ============================================
// UNIFIED STOCK VALUE CALCULATION
// ============================================
export const calculateStockValue = (product) => {
  const quantity = safeNumber(product.quantity);
  if (quantity <= 0) return 0;
  
  let costPerUnit = safeNumber(product.cost_per_unit) || 
                   safeNumber(product.costPerUnit) || 
                   safeNumber(product.unit_cost) || 0;
  
  if (costPerUnit === 0) {
    const costPerPack = safeNumber(product.cost_per_pack);
    const itemsPerPack = safeNumber(product.items_per_pack, 1);
    if (costPerPack > 0 && itemsPerPack > 0) {
      costPerUnit = costPerPack / itemsPerPack;
    }
  }
  
  return costPerUnit * quantity;
};

// ============================================
// DASHBOARD DATA - COMBINES BOTH APIS
// ============================================
export const fetchDashboardData = async (filters = {}) => {
  if (!currentUser) throw new Error('User not authenticated');
  const shopId = currentShop?.shop_id || currentUser?.shop_id;
  if (!shopId) return { stats: {}, trend: { labels: [], values: [] }, recent_sales: [], low_stock: [] };

  const dashboardData = { 
    stats: {}, 
    trend: { labels: [], values: [] }, 
    recent_sales: [], 
    low_stock: [],
    recent_purchases: [],
    recent_returns: [],
    recent_employees: []
  };

  try {
    // Fetch from both APIs in parallel
    const [
      // MECHANICS API - Customers, Suppliers, Employees
      customersRes, 
      suppliersRes, 
      employeesRes,
      
      // COSMETICS API - Products, Sales, Purchases, Returns
      productsRes, 
      todaySalesRes, 
      monthSalesRes, 
      purchasesRes,
      returnsRes
    ] = await Promise.all([
      // Mechanics API calls
      mechanicsRequest('mechanics_customers.php', { action: 'get_records', shop_id: shopId, username: currentUser?.username }),
      mechanicsRequest('mechanics_suppliers.php', { action: 'list', shop_id: shopId }),
      mechanicsRequest('mechanics_employees.php', { action: 'get_records', shop_id: shopId, username: currentUser?.username }),
      
      // Cosmetics API calls
      cosmeticsRequest('cosmetics_products.php', { action: 'get_records', shop_id: shopId, username: currentUser?.username }),
      cosmeticsRequest('cosmetics_sell.php', { action: 'get_records', shop_id: shopId, date: new Date().toISOString().split('T')[0] }),
      cosmeticsRequest('cosmetics_sell.php', { action: 'get_records', shop_id: shopId, 
        start_date: filters.startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], 
        end_date: filters.endDate || new Date().toISOString().split('T')[0] 
      }),
      cosmeticsRequest('cosmetics_purchases.php', { action: 'get_records', shop_id: shopId, 
        start_date: filters.startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], 
        end_date: filters.endDate || new Date().toISOString().split('T')[0] 
      }),
      cosmeticsRequest('cosmetics_return.php', { action: 'get_records', shop_id: shopId, 
        start_date: filters.startDate || new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0], 
        end_date: filters.endDate || new Date().toISOString().split('T')[0] 
      })
    ]);

    // Parse data from both APIs
    const productList = safeArray(productsRes);
    const salesList = safeArray(monthSalesRes);
    const todayList = safeArray(todaySalesRes);
    const purchasesList = safeArray(purchasesRes);
    const returnsList = safeArray(returnsRes);
    const employeesList = safeArray(employeesRes);
    
    // Stats from MECHANICS API
    const totalCustomers = safeNumber(customersRes?.total || customersRes?.data?.length || 0);
    const totalSuppliers = safeNumber(suppliersRes?.suppliers?.length || suppliersRes?.data?.length || 0);
    const totalEmployees = employeesList.length;
    const activeEmployees = employeesList.filter(e => e.status === 'active' || e.status === 'Active').length;
    
    // Stats from COSMETICS API
    const todaySalesTotal = todayList.reduce((sum, sale) => sum + safeNumber(sale.grand_total), 0);
    const todaySalesCount = todayList.length;
    
    const monthSalesTotal = salesList.reduce((sum, sale) => sum + safeNumber(sale.grand_total), 0);
    const monthPurchasesTotal = purchasesList.reduce((sum, p) => sum + safeNumber(p.grand_total), 0);
    
    // Returns stats
    const totalReturns = returnsList.length;
    const totalReturnsAmount = returnsList.reduce((sum, r) => {
      const amount = safeNumber(r.refund_amount) || safeNumber(r.grand_total) || 0;
      return sum + amount;
    }, 0);
    
    // Inventory stats from COSMETICS API
    let inventoryValue = 0;
    let totalProducts = productList.length;
    let lowStockCount = 0;
    
    productList.forEach(p => {
      const quantity = safeNumber(p.quantity);
      const alertQty = safeNumber(p.quantity_alert, 5);
      
      if (quantity <= alertQty && quantity > 0) {
        lowStockCount++;
      }
      
      inventoryValue += calculateStockValue(p);
    });
    
    dashboardData.stats = {
      // From MECHANICS API
      total_customers: totalCustomers,
      total_suppliers: totalSuppliers,
      total_employees: totalEmployees,
      active_employees: activeEmployees,
      
      // From COSMETICS API
      total_products: totalProducts,
      today_sales: todaySalesTotal,
      today_sales_count: todaySalesCount,
      month_sales: monthSalesTotal,
      month_sales_count: salesList.length,
      total_purchases: monthPurchasesTotal,
      inventory_value: inventoryValue,
      low_stock_count: lowStockCount,
      total_returns: totalReturns,
      total_returns_amount: totalReturnsAmount,
    };

    // Sales trend from COSMETICS API
    const salesByDate = {};
    salesList.forEach(sale => {
      const date = sale.date;
      if (date) {
        salesByDate[date] = (salesByDate[date] || 0) + safeNumber(sale.grand_total);
      }
    });
    const dates = Object.keys(salesByDate).sort();
    dashboardData.trend = { 
      labels: dates.map(d => {
        const date = new Date(d);
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }), 
      values: dates.map(d => salesByDate[d]) 
    };
    
    // Recent data from COSMETICS API
    dashboardData.recent_sales = salesList.slice(0, 10);
    dashboardData.recent_purchases = purchasesList.slice(0, 10);
    dashboardData.recent_returns = returnsList.slice(0, 10);
    
    // Recent data from MECHANICS API
    dashboardData.recent_employees = employeesList.slice(0, 10);
    
    // Low stock from COSMETICS API
    dashboardData.low_stock = productList
      .filter(p => {
        const quantity = safeNumber(p.quantity);
        const alertQty = safeNumber(p.quantity_alert, 5);
        return quantity <= alertQty && quantity > 0;
      })
      .slice(0, 20)
      .map(p => ({ 
        id: p.id, 
        product_name: p.product_name || 'Unknown', 
        batch_no: p.batch_no || '-', 
        quantity: p.quantity, 
        unit: p.gunit || p.unit || 'pcs', 
        quantity_alert: p.quantity_alert || 5,
        stock_value: calculateStockValue(p)
      }));

    return dashboardData;
  } catch (error) {
    console.error('Error fetching dashboard data:', error);
    return dashboardData;
  }
};

// ============================================
// CUSTOMERS - MECHANICS API
// ============================================
export const fetchCustomersData = async (shopId, username, filters = {}) => {
  try {
    const response = await mechanicsRequest('mechanics_customers.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const customers = safeArray(response);
    return {
      total: customers.length,
      recent: customers.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching customers:', error);
    return { total: 0, recent: [] };
  }
};

// ============================================
// SUPPLIERS - MECHANICS API
// ============================================
export const fetchSuppliersData = async (shopId, username, filters = {}) => {
  try {
    const response = await mechanicsRequest('mechanics_suppliers.php', {
      action: 'list',
      shop_id: shopId,
      username: username,
    });
    const suppliers = safeArray(response);
    return {
      total: suppliers.length,
      recent: suppliers.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    return { total: 0, recent: [] };
  }
};

// ============================================
// EMPLOYEES - MECHANICS API
// ============================================
export const fetchEmployeesData = async (shopId, username) => {
  try {
    const response = await mechanicsRequest('mechanics_employees.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
    });
    const employees = safeArray(response);
    
    let payments = [];
    try {
      const paymentsRes = await mechanicsRequest('mechanics_employees.php', {
        action: 'get_payments',
        shop_id: shopId,
        limit: 100,
      });
      payments = safeArray(paymentsRes);
    } catch (e) {
      console.error('Error fetching payments:', e);
    }
    
    return {
      total: employees.length,
      active: employees.filter(e => e.status === 'active' || e.status === 'Active').length,
      total_salary_paid: payments.reduce((sum, p) => sum + safeNumber(p.amount), 0),
      recent: employees.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching employees:', error);
    return { total: 0, active: 0, total_salary_paid: 0, recent: [] };
  }
};

// ============================================
// PRODUCTS - COSMETICS API
// ============================================
export const fetchProductsData = async (shopId, username, filters = {}) => {
  try {
    const response = await cosmeticsRequest('cosmetics_products.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const products = safeArray(response);
    
    let inStock = 0;
    let lowStock = 0;
    let outOfStock = 0;
    let totalValue = 0;
    
    products.forEach(p => {
      const quantity = safeNumber(p.quantity);
      const alertQty = safeNumber(p.quantity_alert, 5);
      
      if (quantity <= 0) {
        outOfStock++;
      } else if (quantity <= alertQty) {
        lowStock++;
      } else {
        inStock++;
      }
      
      totalValue += calculateStockValue(p);
    });
    
    return {
      total: products.length,
      inStock,
      lowStock,
      outOfStock,
      totalValue,
      recent: products.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching products:', error);
    return { total: 0, inStock: 0, lowStock: 0, outOfStock: 0, totalValue: 0, recent: [] };
  }
};

// ============================================
// SALES - COSMETICS API
// ============================================
export const fetchSalesData = async (shopId, username, filters = {}) => {
  try {
    const response = await cosmeticsRequest('cosmetics_sell.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const sales = safeArray(response);
    return {
      total: sales.length,
      total_amount: sales.reduce((sum, s) => sum + safeNumber(s.grand_total), 0),
      paid: sales.filter(s => s.payment_status === 'paid').length,
      unpaid: sales.filter(s => s.payment_status === 'unpaid').length,
      partial: sales.filter(s => s.payment_status === 'partial').length,
      recent: sales.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching sales:', error);
    return { total: 0, total_amount: 0, paid: 0, unpaid: 0, partial: 0, recent: [] };
  }
};

// ============================================
// PURCHASES - COSMETICS API
// ============================================
export const fetchPurchasesData = async (shopId, username, filters = {}) => {
  try {
    const response = await cosmeticsRequest('cosmetics_purchases.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const purchases = safeArray(response);
    return {
      total: purchases.length,
      total_amount: purchases.reduce((sum, p) => sum + safeNumber(p.grand_total), 0),
      recent: purchases.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching purchases:', error);
    return { total: 0, total_amount: 0, recent: [] };
  }
};

// ============================================
// RETURNS - COSMETICS API
// ============================================
export const fetchReturnsData = async (shopId, username, filters = {}) => {
  try {
    const response = await cosmeticsRequest('cosmetics_return.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate || '',
      end_date: filters.endDate || '',
    });
    
    let returns = [];
    let totalCount = 0;
    
    if (response && response.status === 'success') {
      if (response.data && Array.isArray(response.data)) {
        returns = response.data;
        totalCount = response.total || returns.length;
      } else if (Array.isArray(response)) {
        returns = response;
        totalCount = returns.length;
      }
    } else if (Array.isArray(response)) {
      returns = response;
      totalCount = returns.length;
    } else if (response && response.data && Array.isArray(response.data)) {
      returns = response.data;
      totalCount = response.total || returns.length;
    }
    
    const totalAmount = returns.reduce((sum, r) => {
      const amount = safeNumber(r.refund_amount) || safeNumber(r.grand_total) || 0;
      return sum + amount;
    }, 0);
    
    return {
      total: totalCount,
      total_amount: totalAmount,
      recent: returns.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching returns:', error);
    return { total: 0, total_amount: 0, recent: [] };
  }
};

// ============================================
// SERVICES - COSMETICS API
// ============================================
export const fetchServicesData = async (shopId, username, filters = {}) => {
  try {
    const response = await cosmeticsRequest('cosmetics_service.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const services = safeArray(response);
    return {
      total: services.length,
      pending: services.filter(s => s.status === 'pending').length,
      in_progress: services.filter(s => s.status === 'in_progress').length,
      completed: services.filter(s => s.status === 'completed').length,
      total_revenue: services.reduce((sum, s) => sum + safeNumber(s.grand_total), 0),
      recent: services.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching services:', error);
    return { total: 0, pending: 0, in_progress: 0, completed: 0, total_revenue: 0, recent: [] };
  }
};

// ============================================
// MOBCHANGE - COSMETICS API
// ============================================
export const fetchMobchangeData = async (shopId, username, filters = {}) => {
  try {
    const response = await cosmeticsRequest('cosmetics_mobchange.php', {
      action: 'get_records',
      shop_id: shopId,
      username: username,
      start_date: filters.startDate,
      end_date: filters.endDate,
    });
    const mobchanges = safeArray(response);
    return {
      total: mobchanges.length,
      pending: mobchanges.filter(m => m.status === 'pending' || m.status === 'in_progress').length,
      completed: mobchanges.filter(m => m.status === 'completed' || m.status === 'delivered').length,
      total_revenue: mobchanges.reduce((sum, m) => sum + safeNumber(m.grand_total), 0),
      recent: mobchanges.slice(0, 10),
    };
  } catch (error) {
    console.error('Error fetching mobchanges:', error);
    return { total: 0, pending: 0, completed: 0, total_revenue: 0, recent: [] };
  }
};

// ============================================
// TRANSACTIONS - COSMETICS API
// ============================================
export const fetchTransactionsData = async (shopId, filters = {}) => {
  try {
    const response = await cosmeticsRequest('cosmetics_transactions.php', {
      action: 'get_transactions',
      shop_id: shopId,
      start_date: filters.startDate,
      end_date: filters.endDate,
      limit: 100,
    });
    const transactions = safeArray(response);
    return {
      customer: transactions.filter(t => t.reference_type === 'customer' || t.type === 'customer'),
      supplier: transactions.filter(t => t.reference_type === 'supplier' || t.type === 'supplier'),
      employee: transactions.filter(t => t.reference_type === 'employee' || t.type === 'employee'),
      expense: transactions.filter(t => t.reference_type === 'expense' || t.type === 'expense'),
    };
  } catch (error) {
    console.error('Error fetching transactions:', error);
    return { customer: [], supplier: [], employee: [], expense: [] };
  }
};

// ============================================
// EXPORT ALL FUNCTIONS
// ============================================
export default {
  setAuthData,
  fetchDashboardData,
  fetchCustomersData,
  fetchSuppliersData,
  fetchEmployeesData,
  fetchProductsData,
  fetchSalesData,
  fetchPurchasesData,
  fetchReturnsData,
  fetchServicesData,
  fetchMobchangeData,
  fetchTransactionsData,
  calculateStockValue,
};